import { useEffect, useState } from "react";
import { adminAuth, fetchAdminProject, getAdminToken, clearAdminToken, formatApiErrorDetail } from "@/villa142/api";
import ReflectionsView from "@/villa142/admin/ReflectionsView";
import EditorView from "@/villa142/admin/EditorView";
import StudiesView from "@/villa142/admin/StudiesView";
import ContributorsView from "@/villa142/admin/ContributorsView";
import BrandView from "@/villa142/admin/BrandView";
import WelcomeMediaView from "@/villa142/admin/WelcomeMediaView";
import UploadsView from "@/villa142/admin/UploadsView";
import HistoryView from "@/villa142/admin/HistoryView";
import "@/villa142/villa142.css";

const AdminGate = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const enter = async (e) => {
    e.preventDefault();
    if (!password.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await adminAuth(password);
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <div className="v142 v142-gate" data-testid="v142-admin-gate">
      <form className="v142-gate-inner" onSubmit={enter}>
        <p className="v142-eyebrow">Studio</p>
        <h1 className="v142-display v142-gate-title">Villa 142 — Studio</h1>
        <div className="v142-gate-row">
          <label htmlFor="v142-admin-password" className="v142-visually-hidden">Studio password</label>
          <input
            id="v142-admin-password"
            data-testid="v142-admin-password-input"
            className="v142-input v142-gate-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Studio password"
          />
          <button className="v142-btn" data-testid="v142-admin-enter-btn" type="submit" disabled={busy}>
            {busy ? "One moment…" : "Enter"}
          </button>
        </div>
        {error && <p className="v142-error" role="alert" data-testid="v142-admin-gate-error">{error}</p>}
      </form>
    </div>
  );
};

const TABS = [
  { id: "reflections", label: "Reflections" },
  { id: "questionnaire", label: "Questionnaire" },
  { id: "studies", label: "Visual studies" },
  { id: "contributors", label: "Contributors" },
  { id: "brand", label: "Brand" },
  { id: "welcome", label: "Welcome & ending media" },
  { id: "uploads", label: "Photo uploads" },
  { id: "history", label: "History" },
];

export default function Villa142Responses() {
  const [phase, setPhase] = useState("checking");
  const [tab, setTab] = useState("reflections");
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Villa 142 — Studio";
  }, []);

  const load = async () => {
    try {
      const data = await fetchAdminProject();
      setProject(data);
      setPhase("view");
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        setPhase("gate");
      } else {
        setError("Could not load. Please try again.");
        setPhase("view");
      }
    }
  };

  useEffect(() => {
    if (getAdminToken()) load();
    else setPhase("gate");
  }, []);

  if (phase === "checking") return <div className="v142 v142-loading"><span className="v142-eyebrow">Villa 142</span></div>;
  if (phase === "gate") return <AdminGate onSuccess={load} />;

  return (
    <div className="v142 v142-admin" data-testid="v142-admin">
      <div className="v142-admin-detail">
        <p className="v142-eyebrow">Studio</p>
        <h1 className="v142-display">Villa 142</h1>
        {error && <p className="v142-error" role="alert">{error}</p>}
        <nav className="v142-tabs" role="tablist" aria-label="Studio sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`v142-tab${tab === t.id ? " is-active" : ""}`}
              data-testid={`v142-tab-${t.id}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab === "reflections" && project && <ReflectionsView data={project} reload={load} />}
        {tab === "questionnaire" && <EditorView />}
        {tab === "studies" && project && <StudiesView studies={project.studies} reload={load} />}
        {tab === "contributors" && <ContributorsView reload={load} />}
        {tab === "brand" && <BrandView />}
        {tab === "welcome" && <WelcomeMediaView />}
        {tab === "uploads" && <UploadsView />}
        {tab === "history" && <HistoryView />}
      </div>
    </div>
  );
}
